import { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/useStore';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { MapPin, ChevronDown, Building2, Plus, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LocationSelector({ collapsed = false }) {
  const { user, token, setUser } = useAuthStore();
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.has_multiple_locations) {
      fetchLocations();
    }
  }, [user]);

  const fetchLocations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/locations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLocations(data.locations || []);
        const current = data.locations?.find(l => l.is_current);
        setCurrentLocation(current);
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const switchLocation = async (locationId) => {
    if (locationId === currentLocation?.id) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/locations/switch/${locationId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCurrentLocation(data.current_location);
        
        // Update user in store
        setUser({
          ...user,
          current_location_id: locationId
        });
        
        toast.success(`Cambiado a ${data.current_location.display_name || data.current_location.name}`);
        
        // Reload page to refresh all data for new location
        window.location.reload();
      } else {
        toast.error('Error al cambiar ubicación');
      }
    } catch (err) {
      toast.error('Error al cambiar ubicación');
    } finally {
      setLoading(false);
    }
  };

  // Don't show if user doesn't have multiple locations
  if (!user?.has_multiple_locations || locations.length <= 1) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={collapsed ? "icon" : "sm"}
          className={cn(
            "w-full justify-start gap-2 bg-accent/50 border-border/50",
            collapsed && "w-10 justify-center"
          )}
          disabled={loading}
          data-testid="location-selector"
        >
          <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
          {!collapsed && (
            <>
              <span className="truncate flex-1 text-left">
                {currentLocation?.location_name || currentLocation?.name || 'Seleccionar'}
              </span>
              <ChevronDown className="h-3 w-3 opacity-50" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64" data-testid="location-dropdown">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Ubicaciones
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {locations.map((location) => (
          <DropdownMenuItem
            key={location.id}
            onClick={() => switchLocation(location.id)}
            className={cn(
              "cursor-pointer",
              location.id === currentLocation?.id && "bg-primary/10"
            )}
            data-testid={`location-${location.id}`}
          >
            <div className="flex items-center gap-2 w-full">
              <MapPin className={cn(
                "h-4 w-4",
                location.id === currentLocation?.id ? "text-primary" : "text-muted-foreground"
              )} />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{location.display_name}</p>
                {location.address && (
                  <p className="text-xs text-muted-foreground truncate">{location.address}</p>
                )}
              </div>
              {location.id === currentLocation?.id && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
        {(user?.role === 'corporate_admin' || user?.role === 'owner') && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => window.location.href = '/settings?tab=locations'}
              className="text-primary cursor-pointer"
            >
              <Plus className="h-4 w-4 mr-2" />
              Agregar ubicación
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
